import { createSlice } from "@reduxjs/toolkit";
import { message } from "antd";
import {
  addEditColumn,
  addEditComment,
  addEditTask,
  addTaskAttachment,
  getCommentsByTaskId,
  getSavedCases,
  getTaskActivitiesByTaskId,
  getTaskAttachmentsByTaskId,
  getTasksByTenantId,
  updateCommentReaction,
  updateFileKey,
} from "./thunks";
import {
  addEditCommentFulfilled,
  addTaskAttachmentFulfilled,
  findTask,
  updateCommentReactionPending,
  updateFileKeyFulfilled,
} from "./helper";

const initialState = {
  columnsByTenants: {},
  loading: true,
  selectedTask: null,
  commentLoading: false,
  attachmentLoading: false,
  activityLoading: false,
  reactions: null,
  casesByTenants: {},
};

const tasksSlice = createSlice({
  name: "tasks",
  initialState,
  reducers: {
    setKeyValue: (state, action) => {
      const { key, value } = action.payload;
      state[key] = value;
    },
    updateColumnsIndex: (state, action) => {
      const { tenant_id, columns } = action.payload;
      state.columnsByTenants[tenant_id] = columns;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getTasksByTenantId.pending, (state) => {
        state.loading = true;
      })
      .addCase(getTasksByTenantId.fulfilled, (state, action) => {
        const { tenant_id } = action.meta.arg;
        const { data } = action.payload;
        state.columnsByTenants[tenant_id] = data.reduce((acc, item) => {
          acc[item.id] = item;
          return acc;
        }, {});
        state.loading = false;
      })
      .addCase(getTasksByTenantId.rejected, (state) => {
        state.loading = false;
      })
      .addCase(addEditColumn.fulfilled, (state, action) => {
        const { data, message: successMessage } = action.payload;
        const { id, tenant_id } = action.meta.arg;

        if (!state.columnsByTenants[tenant_id]) {
          state.columnsByTenants[tenant_id] = {};
        }

        state.columnsByTenants[tenant_id][data.id] = { ...data, tasks: [] };

        message.success(
          successMessage ||
            (id
              ? "Column updated successfully!"
              : "Column added successfully!"),
        );
      })
      .addCase(addEditTask.fulfilled, (state, action) => {
        const { data, message: successMessage } = action.payload;
        const { id, column_id, tenant_id, old_column_id } = action.meta.arg;

        const tasks =
          state.columnsByTenants[tenant_id]?.[column_id]?.tasks || [];

        if (id) {
          if (old_column_id !== column_id) {
            // Remove from old column
            state.columnsByTenants[tenant_id][old_column_id].tasks =
              state.columnsByTenants[tenant_id][old_column_id].tasks.filter(
                (task) => task.id !== data.id,
              );
            // Add to new column
            tasks.push(data);
          } else {
            const index = tasks.findIndex((task) => task.id === data.id);

            if (index !== -1) {
              tasks[index] = { ...tasks[index], ...data };
            }
          }

          message.success(successMessage || "Task updated successfully!");
        } else {
          tasks.push(data);
          message.success(successMessage || "Task added successfully!");
        }

        state.columnsByTenants[tenant_id][column_id].tasks = tasks;
      })
      .addCase(getCommentsByTaskId.pending, (state) => {
        state.commentLoading = true;
      })
      .addCase(getCommentsByTaskId.fulfilled, (state, action) => {
        const { data } = action.payload;
        const task = findTask(state, action);
        if (!task) return;
        task.comments = data;
        state.commentLoading = false;
      })
      .addCase(addEditComment.fulfilled, addEditCommentFulfilled)
      .addCase(updateCommentReaction.pending, updateCommentReactionPending)
      .addCase(updateCommentReaction.fulfilled, addEditCommentFulfilled)
      .addCase(getTaskAttachmentsByTaskId.pending, (state) => {
        state.attachmentLoading = true;
      })
      .addCase(getTaskAttachmentsByTaskId.fulfilled, (state, action) => {
        const task = findTask(state, action);
        if (!task) return;
        const { data } = action.payload;
        task.attachments = data;
        state.attachmentLoading = false;
      })
      .addCase(addTaskAttachment.fulfilled, addTaskAttachmentFulfilled)
      .addCase(updateFileKey.fulfilled, updateFileKeyFulfilled)
      .addCase(getTaskActivitiesByTaskId.pending, (state) => {
        state.activityLoading = true;
      })
      .addCase(getTaskActivitiesByTaskId.fulfilled, (state, action) => {
        const task = findTask(state, action);
        if (!task) return;
        const { data } = action.payload;
        task.activities = [...data, ...(task.activities || [])];
        state.activityLoading = false;
      })
      .addCase(getSavedCases.fulfilled, (state, action) => {
        const { tenant_id } = action.meta.arg;
        const { data } = action.payload;
        state.casesByTenants[tenant_id] = data || [];
        state.loading = false;
      });
  },
});

export const { setKeyValue, updateColumnsIndex } = tasksSlice.actions;

export default tasksSlice.reducer;
